// ═══════════════════════════════════════════════════════════════════════════
// PAGE ADMIN — FICHE D'UN TYPE DE COMITÉ (#admin/committees/:id)
// ═══════════════════════════════════════════════════════════════════════════
//
// Remplace la modale d'édition (août 2026) : la configuration d'un comité a
// dépassé ce qu'une popup peut porter — identité, relances, et désormais les
// guides de réunion applicables.
//
// Trois cartes :
//   1. Identité — code et libellé.
//   2. Relances — délais des trois alertes, plus l'invitation des pilotes.
//   3. Contrôles — quels contrôles s'appliquent, dans quel ordre proposé.
//
// La création reste une modale légère (code + libellé) sur #admin/committees :
// on crée vite, on configure ensuite ici.

import { definePage, navigateTo } from '../router.js';
import { initDragOrder } from '../utils/drag-order.js';

export interface CommitteeEntity {
  id: number;
  code: string;
  name: string;
  position?: number;
  invite_pilot?: boolean;
  alert_actions_update_days?: number;
  alert_support_prep_days?: number;
  alert_cr_extract_days?: number;
}

export interface GuideDefinition {
  id: number;
  code: string | null;
  name: string;
  description: string;
  phase: 'prepare' | 'animate' | 'conclude';
  is_system: boolean;
  position: number;
}

let cdCommittee: CommitteeEntity | null = null;
let cdDefs: GuideDefinition[] = [];
let cdSelection: number[] = [];

function cdPhaseLabel(phase: GuideDefinition['phase']): string {
  return t('pageMeeting.guides.phase.' + phase);
}

function cdFail(err: unknown): void {
  toast(err instanceof Error ? err.message : t('common.error'), 'error');
}

// ── Rendu ────────────────────────────────────────────────────────────────────

/** Rendu pur. Exporté pour être testé sans DOM. */
export function cdIdentityCard(c: CommitteeEntity): string {
  return `<div class="card">
      <div class="card-header"><h2 class="card-title">${esc(t('committees.detail.identity'))}</h2></div>
      <div class="form-row">
        <div class="form-group" style="flex:0 0 140px;min-width:120px">
          <label for="cd-code">${esc(t('committees.field.code.label'))}</label>
          <input type="text" id="cd-code" maxlength="5" value="${esc(c.code)}"
            placeholder="${esc(t('committees.field.code.placeholder'))}"
            style="width:100%;text-transform:uppercase;font-family:var(--font-mono)">
        </div>
        <div class="form-group" style="flex:1">
          <label for="cd-name">${esc(t('committees.col.label'))}</label>
          <input type="text" id="cd-name" value="${esc(c.name)}" style="width:100%"
            placeholder="${esc(t('committees.field.label.placeholder'))}">
        </div>
      </div>
    </div>`;
}

/**
 * Rendu pur. Exporté pour être testé sans DOM.
 *
 * Bloc distinct des relances : l'invitation des pilotes alimente le calcul
 * « Qui inviter au comité », pas les délais d'alerte. Les mêler dans une seule
 * carte laissait croire à un réglage de relance.
 */
export function cdPilotCard(c: CommitteeEntity): string {
  return `<div class="card">
      <div class="card-header"><h2 class="card-title">${esc(
        t('committees.detail.invitePilot'),
      )}</h2></div>
      <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:var(--fs-sm);font-weight:500;color:var(--text)">
        <input type="checkbox" id="cd-invite-pilot"${c.invite_pilot ? ' checked' : ''}>
        <span>${esc(t('committees.field.invite_pilot'))}</span>
      </label>
      <p class="text-sm" style="color:var(--text2);margin:8px 0 0 24px;line-height:1.6">${esc(
        t('committees.field.invite_pilot.note'),
      )}</p>
    </div>`;
}

/** Rendu pur. Exporté pour être testé sans DOM. */
export function cdRemindersCard(c: CommitteeEntity): string {
  const day = (id: string, labelKey: string, value: number): string =>
    `<div class="form-group" style="flex:1;min-width:150px">
        <label for="${id}">${esc(t(labelKey))}</label>
        <input type="number" id="${id}" min="0" max="365" value="${value}" style="width:100%">
      </div>`;
  return `<div class="card">
      <div class="card-header"><h2 class="card-title">${esc(t('committees.field.alerts.title'))}</h2></div>
      <p class="text-sm" style="color:var(--text2);margin:0 0 16px 0;line-height:1.6">${esc(
        t('committees.field.alerts.note'),
      )}</p>
      <div class="form-row" style="margin-bottom:0">
        ${day('cd-alert-actions', 'committees.field.alert_actions_update.label', c.alert_actions_update_days ?? 3)}
        ${day('cd-alert-support', 'committees.field.alert_support_prep.label', c.alert_support_prep_days ?? 5)}
        ${day('cd-alert-cr', 'committees.field.alert_cr_extract.label', c.alert_cr_extract_days ?? 2)}
      </div>
    </div>`;
}

function cdGuideRow(id: number, defs: GuideDefinition[]): string {
  const d = defs.find((x) => x.id === id);
  if (!d) return '';
  const canWrite = canWriteCommitteeDetail();
  return `<tr class="drag-row" draggable="${canWrite}" data-def-id="${d.id}">
      <td style="width:32px;text-align:center">${
        canWrite
          ? `<span class="drag-handle" role="button" aria-label="${esc(
              t('stages.dragdrop.aria_handle'),
            )}">⠿</span>`
          : ''
      }</td>
      <td>${esc(d.name)}</td>
      <td class="text-sm" style="color:var(--text2)">${esc(d.description)}</td>
      <td style="white-space:nowrap">${esc(cdPhaseLabel(d.phase))}</td>
      <td style="text-align:right;white-space:nowrap">
        <button class="btn btn-danger btn-icon cd-remove" data-id="${d.id}" title="${esc(
          t('committees.detail.guideRemove'),
        )}">✕</button>
      </td>
    </tr>`;
}

function cdAvailableHtml(defs: GuideDefinition[], selection: number[]): string {
  const available = defs.filter((d) => !selection.includes(d.id));
  if (!available.length) {
    return `<p class="text-sm" style="color:var(--text3);margin:0">${esc(
      t('committees.detail.allSelected'),
    )}</p>`;
  }
  return `<div style="display:flex;flex-wrap:wrap;gap:8px">${available
    .map(
      (d) =>
        `<button type="button" class="btn btn-secondary btn-sm cd-add" data-id="${d.id}">+ ${esc(
          d.name,
        )}</button>`,
    )
    .join('')}</div>`;
}

/** Rendu pur. Exporté pour être testé sans DOM. */
export function cdGuidesCard(
  defs: GuideDefinition[] = cdDefs,
  selection: number[] = cdSelection,
): string {
  const rows = selection.map((id) => cdGuideRow(id, defs)).join('');
  return `<div class="card" id="cd-guides-card">
      <div class="card-header">
        <h2 class="card-title">${esc(t('committees.detail.guides'))}</h2>
        <a href="#admin/meeting-guides" class="btn btn-secondary btn-sm">${esc(
          t('committees.detail.manageReferential'),
        )}</a>
      </div>
      <p class="text-sm" style="color:var(--text2);margin:0 0 16px 0;line-height:1.6">${esc(
        t('committees.detail.guidesHelp'),
      )}</p>
      ${
        selection.length
          ? `<p style="font-size:12px;color:var(--text3);margin:0 0 10px 0">${esc(
              t('meetingGuides.admin.dragHelp'),
            )}</p>`
          : ''
      }
      <div class="table-wrap"><table>
        <thead><tr>
          <th style="width:32px"></th>
          <th style="min-width:230px">${esc(t('meetingGuides.admin.name'))}</th>
          <th>${esc(t('meetingGuides.admin.description'))}</th>
          <th style="width:110px">${esc(t('meetingGuides.admin.phase'))}</th>
          <th style="width:56px"></th>
        </tr></thead>
        <tbody id="cd-guides-tbody">${
          rows ||
          `<tr><td colspan="5" class="empty">${esc(t('committees.detail.guidesEmpty'))}</td></tr>`
        }</tbody>
      </table></div>
      <div style="margin-top:16px">
        <div style="font-size:var(--fs-xs);font-weight:600;text-transform:uppercase;letter-spacing:0.06em;color:var(--text3);margin-bottom:8px">${esc(
          t('committees.detail.available'),
        )}</div>
        ${cdAvailableHtml(defs, selection)}
      </div>
    </div>`;
}

// `committees.read` garde la route, mais c'est une clé de menu : elle
// n'autorise pas l'écriture. Cette fiche est un formulaire d'édition complet —
// on l'enveloppe donc dans un `<fieldset disabled>`, qui neutralise d'un coup
// tous les champs, sélecteurs et boutons qu'elle contient, y compris ceux
// ajoutés plus tard (revue front §I3). Le bouton Enregistrer et le
// glisser-déposer sont traités à part, hors de ce conteneur.
const canWriteCommitteeDetail = (): boolean =>
  !!(window.hasPermission && window.hasPermission('committees.write'));

function cdPaint(): void {
  if (!cdCommittee) return;
  const cards =
    cdIdentityCard(cdCommittee) +
    cdPilotCard(cdCommittee) +
    cdRemindersCard(cdCommittee) +
    cdGuidesCard();
  setContent(
    canWriteCommitteeDetail()
      ? cards
      : `<fieldset disabled style="border:0;padding:0;margin:0;min-width:0">${cards}</fieldset>`,
  );
  cdWire();
}

// ── Câblage ──────────────────────────────────────────────────────────────────

function cdRepaintGuides(): void {
  const card = document.getElementById('cd-guides-card');
  if (!card) return;
  card.outerHTML = cdGuidesCard();
  cdWire();
}

function cdReadForm(): Record<string, unknown> {
  const val = (id: string): string =>
    (document.getElementById(id) as HTMLInputElement | null)?.value.trim() || '';
  const num = (id: string, fallback: number): number => {
    const n = parseInt(val(id), 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  };
  return {
    code: val('cd-code').toUpperCase(),
    name: val('cd-name'),
    invite_pilot: !!(document.getElementById('cd-invite-pilot') as HTMLInputElement | null)
      ?.checked,
    alert_actions_update_days: num('cd-alert-actions', 3),
    alert_support_prep_days: num('cd-alert-support', 5),
    alert_cr_extract_days: num('cd-alert-cr', 2),
  };
}

function cdWire(): void {
  // Sans `committees.write`, la fiche est en lecture : le `<fieldset disabled>`
  // neutralise déjà les contrôles, on ne câble rien par-dessus.
  if (!canWriteCommitteeDetail()) return;
  document.querySelectorAll<HTMLButtonElement>('.cd-add').forEach((b) =>
    b.addEventListener('click', () => {
      cdSelection = [...cdSelection, Number(b.dataset['id'])];
      cdRepaintGuides();
    }),
  );
  document.querySelectorAll<HTMLButtonElement>('.cd-remove').forEach((b) =>
    b.addEventListener('click', () => {
      cdSelection = cdSelection.filter((id) => id !== Number(b.dataset['id']));
      cdRepaintGuides();
    }),
  );

  const tbody = document.getElementById('cd-guides-tbody');
  if (tbody) {
    initDragOrder({
      container: tbody,
      itemSelector: 'tr.drag-row',
      idKey: 'defId',
      onReorder: (order) => {
        cdSelection = order;
        cdRepaintGuides();
      },
    });
  }
}

async function cdSave(): Promise<void> {
  if (!cdCommittee) return;
  const payload = cdReadForm();
  if (!payload['code'] || !payload['name']) {
    toast(t('committees.error.code_label_required'), 'error');
    return;
  }
  try {
    await api.updateCommittee(cdCommittee.id, payload);
    await api.setCommitteeGuides(cdCommittee.id, { definition_ids: cdSelection });
    // Le cache des référentiels porte les comités : le laisser périmé
    // afficherait l'ancien libellé sur les autres écrans.
    invalidateCommittees();
    toast(t('common.saved'));
    navigateTo('admin/committees', {});
  } catch (err: unknown) {
    cdFail(err);
  }
}

// ── Entrée de page ───────────────────────────────────────────────────────────

pages.adminCommitteeDetail = definePage<'admin/committees/:id'>(async ({ id }) => {
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [committee, defs, selected] = (await Promise.all([
      api.getCommittee(id),
      api.getGuideDefinitions(),
      api.getCommitteeGuides(id),
    ])) as [CommitteeEntity, GuideDefinition[], GuideDefinition[]];
    cdCommittee = committee;
    cdDefs = [...defs].sort((a, b) => a.position - b.position || a.id - b.id);
    cdSelection = selected.map((d) => d.id);

    setHeader(
      `${committee.code} — ${committee.name}`,
      `<a href="#admin/committees" class="btn btn-neutral ml-auto">${esc(t('common.cancel'))}</a>` +
        (canWriteCommitteeDetail()
          ? `<button class="btn btn-primary" id="cd-save" style="margin-left:8px">${esc(t('common.save'))}</button>`
          : ''),
    );
    cdPaint();
    document.getElementById('cd-save')?.addEventListener('click', () => void cdSave());
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});
